import { Box, Paper, Stack, Typography } from '@mui/material'
import { tooltipClasses } from '@mui/material/Tooltip'
import JsonView from '@uiw/react-json-view'
import { C1Button, C1Tooltip } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { AutomationActionItem } from 'components/core/copilot-automation/AutomationActionItem'
import { CELExpressionActionItem } from 'components/core/copilot-cel/CELExpressionActionItem'
import { FunctionActionItem } from 'components/core/copilot-function/FunctionActionItem'
import { PolicyActionItem } from 'components/core/copilot-policy/PolicyActionItem'
import { AgenticAiIcon } from 'components/core/icons/AgenticAiIcon'
import { C1Icon } from 'components/core/icons/C1Icon'
import { SafeMarkdown } from 'components/core/markdown'
import { format } from 'date-fns'
import { ArrowRight, Key } from 'lucide-react'
import { Automation } from 'pbts/c1/api/automations/v1/automations'
import { ToolResultBlock, ToolUseBlock, UIMessage, UIMessage_MessageType, UIMessageActionItem, UIMessageContentBlock, UIMessageRole } from 'pbts/c1/api/conversations/v1/conversations'
import { Policy } from 'pbts/c1/api/policy/v1/policy'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { FC, memo, ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ChatVariant } from './ChatPanel'
import { ChatAccessRequestFormData } from './ChatThread'
import { MessageFeedback } from './MessageFeedback'
import { ViewMessageFeedback } from './ViewMessageFeedback'

type ChatMessageProps = {
    msg: UIMessage
    setIsRequestAccessOpen?: (open: boolean) => void
    setAccessRequestFormData?: (data: ChatAccessRequestFormData) => void
    readOnly?: boolean
    debug?: boolean
    variant?: ChatVariant
    /** Custom renderer for action items - used by mini variant for CEL expressions */
    renderActionItem?: (actionItem: UIMessageActionItem) => ReactNode
    /** When true, thinking messages show "Thinking..." instead of generated content */
    isAutomationAgent?: boolean
    /** When true, show feedback controls on assistant messages (requires !readOnly) */
    showFeedback?: boolean
}

type ParsedResult = {
    error?: string
    result?: string
    thread_id?: string
    question_answer_id?: string
    is_uncertain?: boolean
    relevant_sources?: Array<{
        source_url: string
        title: string
        contains_internal_data: boolean
    }>
}

const ContentToolBlock: FC<{
    data: ToolUseBlock | ParsedResult
    idx: number
}> = ({ data, idx }) =>
    <Box
        key={idx}
        sx={{
            width: '100%',
            p: 1,
            bgcolor: 'grey.100',
            borderRadius: 1,
            maxHeight: '190px',
            overflowX: 'hidden',
            wordBreak: 'break-word',
        }}
    >
        <JsonView value={data} shortenTextAfterLength={0} />
    </Box>

const ToolResultDisplay: FC<{ toolResult: ToolResultBlock, idx: number }> = ({ toolResult, idx }) => {
    const parseResult = (resultString: string): ParsedResult => {
        try {
            return JSON.parse(resultString)
        } catch (err) {
            const error = err as Error
            return { error: error.message, result: resultString }
        }
    }

    const result: ParsedResult = typeof toolResult.result === 'string'
        ? parseResult(toolResult.result)
        : (toolResult.result as ParsedResult)

    return (
        <ContentToolBlock data={result} idx={idx} />
    )
}

const renderContentBlocks = (blocks: UIMessageContentBlock[]): React.ReactNode => {
    return blocks?.map((block, blockIndex) => {
        switch (block.parts.oneofKind) {
            case 'textBlock':
                return <Typography variant="monospace" key={blockIndex}>{block.parts.textBlock.value}</Typography>
            case 'toolUseBlock':
                return <ContentToolBlock key={blockIndex} data={block.parts.toolUseBlock} idx={blockIndex} />
            case 'toolResultBlock':
                return <ToolResultDisplay
                    key={blockIndex}
                    toolResult={block.parts.toolResultBlock}
                    idx={blockIndex}
                />
            default:
                return null
        }
    })
}

type ChatMessageMiniProps = {
    msg: UIMessage
    isUser: boolean
    isAssistant: boolean
    customRenderActionItem?: (actionItem: UIMessageActionItem) => ReactNode
    setIsRequestAccessOpen?: (open: boolean) => void
    setAccessRequestFormData?: (data: ChatAccessRequestFormData) => void
    readOnly?: boolean
    /** When true, thinking messages show "Thinking..." instead of generated content */
    isAutomationAgent?: boolean
    /** When true, show feedback controls on assistant messages (requires !readOnly) */
    showFeedback?: boolean
}

/**
 * Mini thinking message - shows as subtle inline status text with animation.
 */
const ChatMessageMiniThinking: FC<{ content: string }> = ({ content }) => (
    <Box
        sx={{
            py: 1,
            px: 1,
            my: 1,
            alignSelf: 'flex-start',
        }}
    >
        <Stack direction="row" spacing={1} alignItems="center">
            <Box
                sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: 'success.main',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                        '0%, 100%': { opacity: 0.4 },
                        '50%': { opacity: 1 },
                    },
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    color: 'text.secondary',
                    fontStyle: 'italic',
                    fontSize: '0.8rem',
                }}
            >
                {content}
            </Typography>
        </Stack>
    </Box>
)

/**
 * Mini variant of ChatMessage for compact inline chat interfaces.
 * Displays messages without tooltips and with smaller avatars.
 */
const ChatMessageMini: FC<ChatMessageMiniProps> = ({
    msg,
    isUser,
    isAssistant,
    customRenderActionItem,
    setIsRequestAccessOpen,
    setAccessRequestFormData,
    readOnly,
    isAutomationAgent,
    showFeedback,
}) => {
    // Render thinking messages as subtle inline status
    if (msg.type === UIMessage_MessageType.THINKING) {
        const content = isAutomationAgent ? 'Thinking...' : msg.content
        return <ChatMessageMiniThinking content={content} />
    }

    if (msg.type !== UIMessage_MessageType.CONTENT) {
        return null
    }

    return (
        <Box
            sx={{
                py: 1,
                px: 1,
                my: 1,
                borderRadius: C1BorderRadius.regular,
                bgcolor: isUser ? 'primary.extraLight' : 'common.white',
                alignSelf: isUser ? 'flex-end' : 'flex-start',
                maxWidth: '90%',
            }}
        >
            <Stack direction="row" spacing={1} alignItems="flex-start">
                {isAssistant && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 24,
                        minWidth: 24,
                        bgcolor: 'success.extraLight',
                        borderRadius: C1BorderRadius.regular,
                        flexShrink: 0,
                    }}
                    >
                        <AgenticAiIcon fontSize="small" />
                    </Box>
                )}
                <Stack direction="column" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        component="div"
                        sx={{
                            '& p': { m: 0 },
                            '& p:last-child': { mb: 0 },
                        }}
                    >
                        <SafeMarkdown>{msg.content}</SafeMarkdown>
                    </Typography>
                    {msg.actionItem && (customRenderActionItem
                        ? customRenderActionItem(msg.actionItem)
                        : renderDefaultActionItem(
                            msg.actionItem,
                            setIsRequestAccessOpen,
                            setAccessRequestFormData,
                            readOnly,
                        ))}
                    {isAssistant && showFeedback && !readOnly && (
                        <MessageFeedback
                            conversationId={msg.conversationId}
                            messageId={msg.id}
                        />
                    )}
                </Stack>
            </Stack>
        </Box>
    )
}

const renderDefaultActionItem = (
    actionItem: UIMessageActionItem,
    setIsRequestAccessOpen?: (open: boolean) => void,
    setAccessRequestFormData?: (data: ChatAccessRequestFormData) => void,
    readOnly?: boolean,
): React.ReactNode => {
    switch (actionItem.actionItem.oneofKind) {
        case 'accessRequestActionItem': {
            const accessRequestActionItem = actionItem.actionItem.accessRequestActionItem
            return <C1Button
                variant="outlined"
                color="primary"
                disabled={readOnly}
                onClick={(): void => {
                    if (!readOnly && setAccessRequestFormData && setIsRequestAccessOpen) {
                        setIsRequestAccessOpen(true)
                        setAccessRequestFormData({
                            targetUserID: accessRequestActionItem.subjectId,
                            appID: accessRequestActionItem.appId,
                            entitlementID: accessRequestActionItem.entitlementId,
                            justification: accessRequestActionItem.reason,
                        })
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: 'fit-content',
                    p: 4,
                    color: 'info.main',
                }}
            >
                <C1Icon icon={Key} sx={{ color: readOnly ? 'action.disabled' : 'success.main' }} />
                <FormattedMessage
                    id="chat.access_request_item"
                    defaultMessage="{app} {entitlement} requested for {subject_id}"
                    values={{
                        app: accessRequestActionItem.applicationDisplayName,
                        entitlement: accessRequestActionItem.entitlementDisplayName,
                        subject_id: accessRequestActionItem.subjectDisplayName,
                    }}
                />
                <C1Icon icon={ArrowRight} sx={{ color: readOnly ? 'action.disabled' : 'primary.main' }} />
            </C1Button>
        }
        case 'celExpressionActionItem': {
            const celExpression = actionItem.actionItem.celExpressionActionItem
            return <CELExpressionActionItem
                expression={celExpression.expression}
                explanation={celExpression.explanation}
                isValid={celExpression.isValid}
                hideExplanation
            />
        }
        case 'policyDraftActionItem': {
            const policyDraft = actionItem.actionItem.policyDraftActionItem
            // In default rendering (full-page chat), show policy without apply action
            // Apply is handled by custom renderActionItem in PolicyChatAssistant
            return <PolicyActionItem
                policy={policyDraft.policy ?? Policy.create()}
                explanation={policyDraft.explanation}
                isNew={policyDraft.isNew}
            />
        }
        case 'automationDraftActionItem': {
            const automationDraft = actionItem.actionItem.automationDraftActionItem
            // In default rendering (full-page chat), show automation without apply action
            // Apply is handled by custom renderActionItem in AutomationChatAssistant
            return <AutomationActionItem
                automation={automationDraft.automation ?? Automation.create()}
                explanation={automationDraft.explanation}
                isNew={automationDraft.isNew}
            />
        }
        case 'functionDraftActionItem': {
            const functionDraft = actionItem.actionItem.functionDraftActionItem
            // In default rendering (full-page chat), show function without apply action
            // Apply is handled by custom renderActionItem in FunctionChatMiniWindow
            return <FunctionActionItem
                functionId={functionDraft.functionId}
                displayName={functionDraft.displayName}
                description={functionDraft.description}
                explanation={functionDraft.explanation}
                files={functionDraft.files ?? {}}
            />
        }
        default:
            return null
    }
}

/**
 * ChatMessage renders a single message in a chat conversation.
 *
 * Variants:
 * - 'default': Full-featured message with avatars, feedback, and tooltips
 * - 'mini': Compact message for embedded chat, no user avatars, smaller AI avatar
 */
export const ChatMessage: FC<ChatMessageProps> = memo(({
    msg,
    setIsRequestAccessOpen,
    setAccessRequestFormData,
    readOnly,
    debug,
    variant = 'default',
    renderActionItem: customRenderActionItem,
    isAutomationAgent,
    showFeedback,
}) => {
    const isUser = msg.role === UIMessageRole.UI_MESSAGE_ROLE_USER
    const isAssistant = msg.role === UIMessageRole.UI_MESSAGE_ROLE_ASSISTANT
    const isMini = variant === 'mini'

    // Mini variant: simpler rendering without tooltips (handles thinking messages inline)
    if (isMini) {
        return <ChatMessageMini
            msg={msg}
            isUser={isUser}
            isAssistant={isAssistant}
            customRenderActionItem={customRenderActionItem}
            setIsRequestAccessOpen={setIsRequestAccessOpen}
            setAccessRequestFormData={setAccessRequestFormData}
            readOnly={readOnly}
            isAutomationAgent={isAutomationAgent}
            showFeedback={showFeedback}
        />
    }

    // Default variant styling
    const defaultStyles = {
        py: 2,
        px: 3,
        my: 3,
        avatarSize: 40,
        spacing: 2,
    }

    const renderDefaultContent = (): React.ReactElement => {
        switch (msg.type) {
            case UIMessage_MessageType.CONTENT:
                return <Stack
                    direction="column"
                    sx={{
                        alignSelf: isUser ? 'flex-end' : 'flex-start',
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            py: defaultStyles.py,
                            px: defaultStyles.px,
                            my: defaultStyles.my,
                            borderRadius: C1BorderRadius.large,
                            bgcolor: isUser ? 'primary.extraLight' : 'transparent',
                            alignSelf: isUser ? 'flex-end' : 'flex-start',
                            width: 'auto',
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={defaultStyles.spacing}
                            width="100%"
                        >
                            {isAssistant &&
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: `${defaultStyles.avatarSize}px`,
                                    minWidth: `${defaultStyles.avatarSize}px`,
                                    bgcolor: 'success.extraLight',
                                    borderRadius: C1BorderRadius.large,
                                }}
                                >
                                    <AgenticAiIcon fontSize="medium" />
                                </Box>
                            }
                            <Stack direction="column" spacing={2} alignItems="top" sx={{ width: '100%' }}>
                                <SafeMarkdown>{msg.content}</SafeMarkdown>
                                {msg.actionItem && (customRenderActionItem
                                    ? customRenderActionItem(msg.actionItem)
                                    : renderDefaultActionItem(
                                        msg.actionItem,
                                        setIsRequestAccessOpen,
                                        setAccessRequestFormData,
                                        readOnly,
                                    ))}
                                {isAssistant && !readOnly && <MessageFeedback
                                    conversationId={msg.conversationId}
                                    messageId={msg.id}
                                />}
                                {isAssistant && readOnly && msg.feedback && <ViewMessageFeedback feedback={msg.feedback} />}
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>
            case UIMessage_MessageType.DEBUG:
                if (!debug) return <></>
                return <Paper
                    elevation={0}
                    sx={{
                        py: 3,
                        px: 3,
                        my: 3,
                        mx: 2,
                        borderRadius: C1BorderRadius.large,
                        bgcolor: 'grey.50',
                        alignSelf: 'flex-start',
                        width: 'auto',
                    }}
                >
                    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '40px',
                            minWidth: '40px',
                            bgcolor: 'success.extraLight',
                            borderRadius: C1BorderRadius.large,
                        }}
                        >
                            <AgenticAiIcon fontSize="medium" />
                        </Box>
                        <Stack direction="column" spacing={2}>
                            {renderContentBlocks(msg.blocks)}
                        </Stack>
                    </Stack>
                </Paper>
            default:
                return <></>
        }
    }

    // Default variant: full rendering with tooltips
    const title = msg.createdAt && <Typography variant="subtitle2">
        {format(Timestamp.toDate(msg.createdAt), 'p')}
    </Typography>

    return <C1Tooltip
        title={title}
        placement={isUser ? 'top-end' : 'top-start'}
        arrow={false}
        slotProps={{
            popper: {
                sx: {
                    zIndex: 10,
                    [`.${tooltipClasses.tooltip}`]: {
                        bgcolor: 'transparent',
                        color: 'text.secondary',
                    },
                },
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -30],
                        },
                    },
                ],
            },
        }}
    >
        {renderDefaultContent()}
    </C1Tooltip>
})
